"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";

export type Theme = "light" | "gray";

export interface ThemeColors {
  backgroundColor: string;
  paragraphTextColor: string;
  paragraphTextOtherColor: string;
  logoColor: string;
  footerLinkColor: string;
  tagBackgroundColor: string;
  tagBorderColor: string;
  tagTextColor: string;
  headerTextColor: string;
  headerDateColor: string;
  authorColor: string;
  commentTitleColor: string;
  commentLabelColor: string;
  commentTextColor: string;
  commentBorderColor: string;
  commentFocusBorderColor: string;
  commentButtonColor: string;
  commentButtonBorderColor: string;
  commentCardBackgroundColor: string;
  commentDateColor: string;
  commentEmojiSelectedColor: string;
  commentCardBorderColor: string;
  commentLeaveSectionBackgroundColor: string;
}

const themes: Record<Theme, ThemeColors> = {
  light: {
    backgroundColor: "white",
    paragraphTextColor: "text-black",
    paragraphTextOtherColor: "[&_*]:text-black",
    logoColor: "text-black-400",
    footerLinkColor: "text-black",
    tagBackgroundColor: "rgba(200,200,200,0.3)",
    tagBorderColor: "border-black",
    tagTextColor: "text-black",
    headerTextColor: "text-black",
    headerDateColor: "text-black",
    authorColor: "text-black",
    commentTitleColor: "text-black",
    commentLabelColor: "text-gray-700",
    commentTextColor: "text-gray-700",
    commentBorderColor: "border-black",
    commentFocusBorderColor: "border-black",
    commentButtonColor: "text-black",
    commentButtonBorderColor: "border-black",
    commentCardBackgroundColor: "#c8c8c84d",
    commentDateColor: "text-gray-400",
    commentEmojiSelectedColor: "bg-gray-300",
    commentCardBorderColor: "border-transparent",
    commentLeaveSectionBackgroundColor: "bg-transparent",
  },
  gray: {
    backgroundColor: "#efefef",
    paragraphTextColor: "text-[#31542B]",
    paragraphTextOtherColor: "[&_*]:text-[#31542B]",
    logoColor: "text-[#4a5753]",
    footerLinkColor: "text-[#4a5753]",
    tagBackgroundColor: "rgba(48, 67, 56, 0.4)",
    tagBorderColor: "border-none",
    tagTextColor: "text-[#efefef]",
    headerTextColor: "text-[#4a5753]",
    headerDateColor: "text-[#4a5753]",
    authorColor: "text-[#4a5753]",
    commentTitleColor: "text-[#4a5753]",
    commentLabelColor: "text-[#4a5753]",
    commentTextColor: "text-[#efefef]",
    commentBorderColor: "border-[#4a5753]",
    commentFocusBorderColor: "border-[#4a5753]",
    commentButtonColor: "text-[#4a5753]",
    commentButtonBorderColor: "border-[#4a5753]",
    commentCardBackgroundColor: "#30433866", // "rgba(48, 67, 56, 0.252)", // rgb(251, 251, 251) is a light gray color
    commentDateColor: "text-[#e0e0e0]",
    commentEmojiSelectedColor: "bg-[#30433866]",
    commentCardBorderColor: "border-none",
    commentLeaveSectionBackgroundColor: "bg-[#efefef]",
  },
};

interface ThemeContextType {
  theme: Theme;
  themeColors: ThemeColors;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>("light");
  const themeColors = themes[theme];

  return (
    <ThemeContext.Provider value={{ theme, themeColors, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
